import { type ReactNode, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { LayoutListContext, type LayoutOption } from "./layout-list-context";
import type { Layout, Tree } from "./layout-utils";

type LayoutListProviderProps = {
  children: ReactNode;
  setLayouts: (layouts: Layout[]) => void;
  layouts: Layout[];
  selectedLayoutId: string;
  setSelectedLayoutId: (id: string) => void;
};

export const LayoutListProvider = ({
  children,
  setLayouts,
  layouts,
  selectedLayoutId,
  setSelectedLayoutId,
}: LayoutListProviderProps) => {
  const addLayout = useCallback(
    (name: string) => {
      const id = uuidv4();
      const newLayout: Layout = {
        version: "1",
        data: { id, name, tree: { type: "row", children: [] } },
      };

      setLayouts([...layouts, newLayout]);
      return id;
    },
    [layouts, setLayouts]
  );

  const updateLayoutName = useCallback(
    (id: string, name: string) => {
      const newLayouts = layouts.map((layout) => {
        if (layout.data.id === id) {
          return { ...layout, data: { ...layout.data, name } };
        }
        return layout;
      });

      setLayouts(newLayouts);
    },
    [layouts, setLayouts]
  );

  const updateLayoutTree = useCallback(
    (id: string, tree: Tree) => {
      const newLayouts = layouts.map((layout) => {
        if (layout.data.id === id) {
          return { ...layout, data: { ...layout.data, tree } };
        }
        return layout;
      });

      setLayouts(newLayouts);
    },
    [layouts, setLayouts]
  );

  const deleteLayout = useCallback(
    (id: string) => {
      const newLayouts = layouts.filter((layout) => layout.data.id !== id);
      setLayouts(newLayouts);
    },
    [layouts, setLayouts]
  );

  const options: LayoutOption[] = useMemo(() => {
    return layouts.map((layout) => ({
      id: layout.data.id,
      name: layout.data.name,
    }));
  }, [layouts]);

  const selectedLayout = useMemo(() => {
    const layout = layouts.find((l) => l.data.id === selectedLayoutId);

    return layout ?? layouts[0];
  }, [layouts, selectedLayoutId]);

  return (
    <LayoutListContext.Provider
      value={{
        options,
        addLayout,
        updateLayoutName,
        updateLayoutTree,
        deleteLayout,
        selectedLayout,
        setSelectedLayoutId,
      }}
    >
      {children}
    </LayoutListContext.Provider>
  );
};
